import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { GlobeHemisphereEast, Phone, ChatCircleText, Printer as Fax, WhatsappLogo, Check, PhoneCall, MagnifyingGlass, CaretDown, Trash, Warning } from "@phosphor-icons/react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { id: "local", label: "Local" },
  { id: "tollfree", label: "Toll-Free" },
  { id: "mobile", label: "Mobile" },
];

// Assigned US area codes in the 201–760 range
const US_AREA_CODES = [
  "201","202","203","205","206","207","208","209","210","212","213","214","215","216","217","218","219","220","223","224","225","227","228","229",
  "231","234","239","240","248","251","252","253","254","256","260","262","267","269","270","272","276","279","281","283",
  "301","302","303","304","305","307","308","309","310","312","313","314","315","316","317","318","319","320","321","323","325","326","330","331","332","334","336","337","339","341","346","347","351","352","360","361","364","380","385","386",
  "401","402","404","405","406","407","408","409","410","412","413","414","415","417","419","423","424","425","430","432","434","435","440","442","443","445","447","458","463","464","469","470","472","475","478","479","480","484",
  "501","502","503","504","505","507","508","509","510","512","513","515","516","517","518","520","530","531","534","539","540","541","551","559","561","562","563","564","567","570","571","572","573","574","575","580","585","586",
  "601","602","603","605","606","607","608","609","610","612","614","615","616","617","618","619","620","623","626","628","629","630","631","636","640","641","646","650","651","656","657","660","661","662","667","669","678","680","681","682","689",
  "701","702","703","704","706","707","708","712","713","714","715","716","717","718","719","720","724","725","726","727","731","732","734","737","740","743","747","754","757","760",
  "762","763","765","769","770","772","773","774","775","779","781","785","786","787",
  "801","802","803","804","805","806","808","810","812","813","814","815","816","817","818","820","828","830","831","832","835","838","839","843","845","847","848","850","854","856","857","858","859","860","862","863","864","865","870","872","878",
  "901","903","904","906","907","908","909","910","912","913","914","915","916","917","918","919","920","925","928","929","930","931","934","936","937","938","939","940","941","943","945","947","948","949","951","952","954","956","959","970","971","972","973","975","978","979","980","983","984","985","986","989",
];

export default function Numbers() {
  const qc = useQueryClient();
  const [countryCode, setCountryCode] = useState("US");
  const [numberType, setNumberType] = useState("local");
  const [areaCode, setAreaCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [manualNum, setManualNum] = useState("");
  const { isStaff } = useAuth();

  const { data: countriesData } = useQuery({ queryKey: ["did-countries"], queryFn: async () => (await api.get("/dids/countries")).data });
  const countries = countriesData?.countries || [];
  const country = useMemo(() => countries.find((c) => c.code === countryCode) || null, [countries, countryCode]);

  const { data: search, isFetching } = useQuery({
    queryKey: ["did-search", applied?.country, applied?.numberType, applied?.areaCode],
    queryFn: async () => (await api.get(`/dids/search?country=${applied.country}&number_type=${applied.numberType}&provider=telnyx${applied.areaCode ? `&area_code=${applied.areaCode}` : ""}`)).data,
    enabled: !!applied,
  });
  const { data: mine } = useQuery({ queryKey: ["dids-mine"], queryFn: async () => (await api.get("/dids/mine")).data });

  const runSearch = () => setApplied({ country: countryCode, numberType, areaCode: countryCode === "US" ? areaCode : "" });

  // Deep-link (?open=1) from a tapped SMS/WhatsApp link — show live inventory immediately.
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("open")) setApplied({ country: "US", numberType: "local", areaCode: "" });
  }, []); // eslint-disable-line

  const [smsBusy, setSmsBusy] = useState("");
  const releaseNumber = async () => {
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      await api.post("/dids/release", { number: releaseTarget.number });
      toast.success(`${releaseTarget.number} released — billing stops.`);
      qc.invalidateQueries({ queryKey: ["dids-mine"] });
      setReleaseTarget(null);
      setManualNum("");
    } catch (e) { toast.error(apiError(e)); } finally { setReleasing(false); }
  };

  const purchase = async () => {
    if (!selected || !country) return;
    setBusy(true);
    try {
      await api.post("/dids/purchase", {
        provider: search?.provider || "telnyx", number: selected.number, country: country.code,
        number_type: numberType, monthly_price_cents: selected.monthly_price_cents,
      });
      toast.success(`${selected.number} activated${search?.mode === "LIVE" ? " · live" : " (preview)"}`);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["dids-mine"] });
      setSelected(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const smsReady = async (number) => {
    setSmsBusy(number);
    try {
      const { data } = await api.post("/dids/sms-ready", { number });
      toast.success(`${number} is now SMS-ready${data.sms_enabled ? " ✓" : ""}`);
      qc.invalidateQueries({ queryKey: ["dids-mine"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSmsBusy(""); }
  };
  const smsReadyAll = async () => {
    setSmsBusy("all");
    try {
      const { data } = await api.post("/number-inventory/sms-ready-all");
      toast.success(`SMS-ready: ${data.sms_ready}/${data.total} numbers connected`);
      qc.invalidateQueries({ queryKey: ["dids-mine"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSmsBusy(""); }
  };

  const caps = (c) => [
    c.voice && { icon: Phone, k: "Voice" }, c.sms && { icon: ChatCircleText, k: "SMS" },
    c.fax && { icon: Fax, k: "Fax" }, c.whatsapp_eligible && { icon: WhatsappLogo, k: "WhatsApp" },
  ].filter(Boolean);

  const selectCls = "w-full appearance-none rounded-xl border border-input bg-card px-3 py-2.5 pr-9 text-sm outline-none focus:border-primary";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Buy Numbers</h1>
        <p className="mt-2 text-muted-foreground">Choose a country, type and area code — then activate. Live global number inventory.</p>
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Country</span>
            <div className="relative">
              <select data-testid="country-select" value={countryCode}
                onChange={(e) => { setCountryCode(e.target.value); setApplied(null); setAreaCode(""); }} className={selectCls}>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name} {c.dial}</option>)}
              </select>
              <CaretDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Type</span>
            <div className="relative">
              <select data-testid="type-select" value={numberType} onChange={(e) => setNumberType(e.target.value)} className={selectCls}>
                {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <CaretDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Area code{countryCode !== "US" ? " (US only)" : ""}</span>
            <div className="relative">
              <select data-testid="areacode-select" value={areaCode} onChange={(e) => setAreaCode(e.target.value)}
                disabled={countryCode !== "US"} className={`${selectCls} disabled:opacity-50`}>
                <option value="">Any area code</option>
                {US_AREA_CODES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <CaretDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>
          </label>

          <div className="flex items-end">
            <button data-testid="search-numbers" onClick={runSearch}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">
              <MagnifyingGlass size={16} weight="bold" /> Search
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {applied && (
        <div className="space-y-3">
          {isFetching && <p className="text-sm text-muted-foreground">Searching {country?.flag} {country?.name}…</p>}
          <div className="grid gap-3 md:grid-cols-2">
            {(search?.numbers || []).map((n) => (
              <button key={n.id} data-testid={`number-${n.id}`} onClick={() => setSelected(n)}
                className="flex items-center justify-between rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary">
                <div>
                  <p className="font-mono-metric text-lg font-semibold">{n.number}</p>
                  <div className="mt-1.5 flex gap-1.5">
                    {caps(n.capabilities).map((c) => (
                      <span key={c.k} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        <c.icon size={11} weight="bold" /> {c.k}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono-metric font-semibold">{money(n.monthly_price_cents)}</p>
                  <p className="text-[10px] text-muted-foreground">/mo</p>
                </div>
              </button>
            ))}
          </div>
          {!isFetching && search && search.numbers?.length === 0 && (
            <div data-testid="no-inventory" className="rounded-2xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
              No inventory for {country?.flag} {country?.name}{applied.areaCode ? ` · area code ${applied.areaCode}` : ""}. Try another area code or type.
            </div>
          )}
        </div>
      )}

      {/* Confirm activation */}
      {selected && country && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="confirm-modal" onClick={() => setSelected(null)}>
          <div className="w-full max-w-md rounded-3xl border border-border bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary"><GlobeHemisphereEast size={28} weight="duotone" /></div>
            <h2 className="font-display text-xl font-semibold">Confirm activation</h2>
            <dl className="mt-4 space-y-3 text-sm">
              {[["Number", selected.number], ["Country", `${country.flag} ${country.name}`], ["Type", numberType]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium">{v}</dd></div>
              ))}
              <div className="flex justify-between border-t border-border pt-3"><dt className="font-semibold">Monthly total</dt><dd className="font-mono-metric font-semibold">{money(selected.monthly_price_cents)}</dd></div>
            </dl>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setSelected(null)} className="rounded-full border border-border px-5 py-3 text-sm font-medium">Cancel</button>
              <button data-testid="activate-number" onClick={purchase} disabled={busy}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
                <Check size={18} weight="bold" /> {busy ? "Activating…" : "Activate Number"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* owner-only: release ANY account number (even ones not in the in-app pool) */}
      {isStaff && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 sm:p-5" data-testid="owner-release-panel">
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-destructive">Release a carrier number</h2>
          <p className="mt-1 text-xs text-muted-foreground">Owner tool — release any number on your iYeneh carrier account (stops billing). Use for numbers that aren't listed in your pool below.</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <input data-testid="owner-release-input" value={manualNum} onChange={(e) => setManualNum(e.target.value)}
              placeholder="+1XXXXXXXXXX" className="min-w-[220px] flex-1 rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-destructive" />
            <button data-testid="owner-release-btn" disabled={!manualNum.startsWith("+")}
              onClick={() => setReleaseTarget({ number: manualNum.trim(), id: "manual" })}
              className="rounded-full bg-destructive px-5 py-2.5 text-sm font-semibold text-destructive-foreground disabled:opacity-50">
              Release
            </button>
          </div>
          <button data-testid="sms-ready-all-btn" onClick={smsReadyAll} disabled={smsBusy === "all"}
            className="mt-3 rounded-full border border-primary/50 px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50">
            {smsBusy === "all" ? "Connecting…" : "📩 Make all my numbers SMS-ready"}
          </button>
        </div>
      )}

      {/* release confirm */}
      {releaseTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="release-modal" onClick={() => setReleaseTarget(null)}>
          <div className="w-full max-w-md rounded-3xl border border-border bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive"><Warning size={28} weight="duotone" /></div>
            <h2 className="font-display text-xl font-semibold">Release {releaseTarget.number}?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This permanently releases the number and <strong>stops its billing</strong>. It cannot be undone — you may not be able to get this exact number back. Any calls, SMS or fax pointed at it will stop working.
            </p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setReleaseTarget(null)} className="rounded-full border border-border px-5 py-3 text-sm font-medium">Cancel</button>
              <button data-testid="confirm-release" onClick={releaseNumber} disabled={releasing}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-destructive py-3 font-semibold text-destructive-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
                <Trash size={18} weight="bold" /> {releasing ? "Releasing…" : "Release number"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* my numbers */}
      {mine?.dids?.length > 0 && (
        <div>
          <h2 className="mb-3 mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">My Numbers</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {mine.dids.map((d) => (
              <div key={d.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`mine-did-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono-metric font-semibold">{d.number}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{d.country} · {d.type} · <span className="text-emerald-600">{d.status}</span>{d.sms_ready && <span className="text-primary"> · SMS ✓</span>}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button data-testid={`sms-ready-${d.id}`} onClick={() => smsReady(d.number)} disabled={smsBusy === d.number}
                      title="Make this number SMS-ready (send & receive texts)"
                      className="rounded-full border border-border p-2 text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50">
                      <ChatCircleText size={15} weight="bold" />
                    </button>
                    <button data-testid={`release-${d.id}`} onClick={() => setReleaseTarget(d)}
                      title="Release this number"
                      className="rounded-full border border-border p-2 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive">
                      <Trash size={15} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/app/telephony" data-testid="numbers-call-setup"
            className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-primary/40 bg-accent px-4 py-3 text-sm transition-colors hover:border-primary">
            <span className="flex items-center gap-2 font-medium"><PhoneCall size={18} weight="duotone" className="text-primary" /> Receive calls on this device — rings the app, falls back to your mobile</span>
            <span className="shrink-0 font-semibold text-primary">Set up →</span>
          </Link>
        </div>
      )}
    </div>
  );
}
